export interface FormWorkflowPlan {
    form: PlannedForm;
    workflow: PlannedWorkflow;
}

export interface PlannedFormUse {
    registration?: boolean;
    eventSurvey?: boolean;
    eventCreation?: boolean;
    orgCreation?: boolean;
    orgEdit?: boolean;
    eventEdit?: boolean;
    electionCandidate?: boolean;
    orgJoin?: boolean;
}

export interface PlannedForm {
    name: string;
    description: string;
    use?: PlannedFormUse;
    orgId?: number;
    orgName?: string;
    enabled?: boolean;
    fields: PlannedField[];
    showHideRules: PlannedShowHideRule[];
    validationRules: PlannedValidationRule[];
}

export interface PlannedField {
    fieldDefinitionId?: number;
    fieldLabel: string;
    fieldType: string;
    answerType?: string;
    required: boolean;
    infoText: string;
    options: string[];
    sortOrder: number;
    minLength?: number;
    maxLength?: number;
    regexValid?: string;
    defaultValue?: string;
    defaultHidden?: boolean;
    custAtrbId?: number;
    custAtrbName?: string;
}

export interface PlannedShowHideRule {
    rootRuleId?: number;
    ruleName: string;
    conditions: PlannedCondition[];
    action: string;
    targetFieldLabel: string;
}

export interface PlannedValidationRule {
    rootRuleId?: number;
    ruleName: string;
    conditions: PlannedCondition[];
    validationMessage: string;
}

export interface PlannedWorkflow {
    name: string;
    description: string;
    stages: PlannedStage[];
    transitions: PlannedTransition[];
    stageRules?: PlannedStageRule[];
    stateRules?: PlannedStateRule[];
    actionRules?: PlannedActionRule[];
}

export interface PlannedStage {
    stageName: string;
    description: string;
    isStartStage: boolean;
    isTerminal: boolean;
    isAccumulator: boolean;
    defaultEnabled?: boolean;
    states: PlannedApprovalState[];
    actions: PlannedAction[];
}

export interface PlannedApprovalState {
    stateName: string;
    defaultEnabled?: boolean;
    approvalGroupId?: number;
    approvalGroupName?: string;
    contactIds?: number[];
    contactNames?: string[];
    orgRoleIds?: number[];
    orgRoleNames?: string[];
}

export interface PlannedAction {
    actionCode: string;
    transitionType: string;
    description: string;
    defaultEnabled?: boolean;
    inputs: Record<string, string>;

    // All spawn modes
    spawnActionHandling?: string;
    spawnReuseSameItem?: boolean;

    // Process Composition mode (spawnReuseSameItem=true)
    spawnWorkflowProcessId?: number;
    spawnWorkflowProcessName?: string;

    // Submitter mode (spawnReuseSameItem=false, no iterator)
    spawnChildFormId?: number;
    spawnChildFormName?: string;

    // Iterator mode (spawnReuseSameItem=false, with iterator)
    spawnIteratorFieldLabel?: string;
    spawnDispatchFieldType?: string;
    dispatchMappings?: PlannedDispatchMapping[];
}

export interface PlannedDispatchMapping {
    dispatchValue: string;
    dispatchValueLabel: string;
    targetFormId: number;
    targetFormName: string;
}

export interface PlannedTransition {
    fromStageName: string;
    toStageName: string;
    transitionType: string;
    transitionLabel: string;
}

export interface PlannedCondition {
    sourceFieldLabel: string;
    operator: string;
    values: string[];
}

export interface PlannedStageRule {
    ruleName: string;
    conditions: PlannedCondition[];
    action: string;
    targetStageName: string;
}

export interface PlannedStateRule {
    ruleName: string;
    conditions: PlannedCondition[];
    action: string;
    targetStageName: string;
    targetStateName: string;
}

export interface PlannedActionRule {
    ruleName: string;
    conditions: PlannedCondition[];
    action: string;
    targetStageName: string;
    targetActionCode: string;
    targetTransitionType: string;
    targetActionDescription?: string;
}

export interface PlanResponse {
    chatResponse: string;
    plan: FormWorkflowPlan | null;
}

export interface BuildResult {
    ingestionPointId: number | null;
    workflowProcessId: number | null;
    warnings: string[];
    enabled?: boolean;
}

export interface ChatMessage {
    role: "user" | "assistant";
    content: string;
}

export interface FormEditDiff {
    addedFields: string[];
    removedFields: string[];
    updatedFields: string[];
    ruleChanges: string[];
    scopeChanges?: string[];
}

const FORM_USE_LABELS: { key: keyof PlannedFormUse; label: string }[] = [
    { key: "registration", label: "Registration" },
    { key: "eventSurvey", label: "Event Survey" },
    { key: "eventCreation", label: "Event Creation" },
    { key: "orgCreation", label: "Org Creation" },
    { key: "orgEdit", label: "Org Edit" },
    { key: "eventEdit", label: "Event Edit" },
    { key: "electionCandidate", label: "Election Candidate" },
    { key: "orgJoin", label: "Org Join" },
];

export function plannedFormScopeLines(form: PlannedForm | null | undefined): string[] {
    if (!form) {
        return [];
    }
    const lines: string[] = [];
    const useLabels = FORM_USE_LABELS.filter((entry) => form.use?.[entry.key]).map((entry) => entry.label);
    if (useLabels.length) {
        lines.push("Use: " + useLabels.join(", "));
    }
    if (form.orgId != null && form.orgName) {
        lines.push("Organization: " + form.orgName);
    } else if (form.orgId != null) {
        lines.push("Scoped to an organization");
    } else if (form.orgName) {
        lines.push("Organization was not resolved");
    }
    if (form.enabled === true) {
        lines.push("Form and workflow enabled when built");
    }
    return lines;
}

export interface EditPlanResponse {
    chatResponse: string;
    plan: PlannedForm | null;
    diff: FormEditDiff | null;
}
